import { useState, type FormEvent } from 'react';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const aboutImage =
  'https://readdy.ai/api/search-image?query=Warm%20illustration%20of%20a%20modern%20creative%20team%20of%20editors%20reading%20comics%20around%20a%20wooden%20table%2C%20laptops%20and%20storyboards%20on%20the%20table%2C%20cozy%20office%20with%20manga%20posters%20on%20the%20walls%2C%20soft%20golden%20lighting%2C%20friendly%20atmosphere%2C%20bold%20clean%20linework%2C%20warm%20orange%20and%20amber%20color%20grading%2C%20clean%20minimal%20background%2C%20high%20quality%20digital%20painting%2C%20no%20text%2C%20no%20watermark&width=1200&height=600&seq=about-team-culture-01&orientation=landscape';

const stats = [
  { value: '3200+', label: '在库作品' },
  { value: '860万+', label: '注册读者' },
  { value: '150+', label: '每日更新话数' },
  { value: '120万', label: '日均活跃读者' },
];

const honors = [
  { year: '2026', title: '数字阅读内容生态优秀案例', desc: '凭借阅读元宇宙社交玩法获行业认可' },
  { year: '2025', title: '读者最喜爱免费漫画平台', desc: '由百万读者在线投票评选产生' },
  { year: '2024', title: '漫画阅读平台创新奖', desc: '下拉式阅读与社区化追更体验获得高度评价' },
];

const visions = [
  { icon: 'ri-eye-line', title: '让好漫画被看见', desc: '坚持内容为王，用心推荐每一部值得被发现的宝藏作品。' },
  { icon: 'ri-heart-3-line', title: '让阅读更有温度', desc: '以阅读元宇宙社交连接千万读者，让追更不再是孤岛。' },
  { icon: 'ri-rocket-2-line', title: '持续进化阅读体验', desc: '免费无删减 + 丝滑下拉阅读，把体验打磨到极致。' },
];

const contactItems = [
  { icon: 'ri-map-pin-2-line', label: '公司地址', value: '北京市朝阳区建国路88号漫画大厦8层' },
  { icon: 'ri-mail-line', label: '联系邮箱', value: 'support@57mh.net' },
  { icon: 'ri-phone-line', label: '客服电话', value: '400-888-5757' },
  { icon: 'ri-time-line', label: '客服时间', value: '周一至周日 9:00 - 21:00' },
];

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9v9t3mth95mubjtu500';

export default function About() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');
    setStatus('submitting');
    setFormError('');
    try {
      const payload = new URLSearchParams(formData as unknown as Record<string, string>).toString();
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload,
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg)) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg && serverMsg !== 'OK' ? serverMsg : '提交失败，请检查信息后重试');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试');
    }
  };

  const jsonLd = [
    {
      '@type': 'AboutPage',
      '@id': 'https://readfunny0.github.io/about#aboutpage',
      url: 'https://readfunny0.github.io/about',
      name: '关于57漫画网',
      description: '57漫画网是专注下拉式免费阅读的漫画平台，提供阅读元宇宙社交体验，在虚拟漫画咖啡馆中与其他读者化身交流阅读心得。',
      isPartOf: { '@id': 'https://readfunny0.github.io/#website' },
      about: { '@id': 'https://readfunny0.github.io/#organization' },
      inLanguage: 'zh-CN',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://readfunny0.github.io/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://readfunny0.github.io/about' },
      ],
    },
  ];

  return (
    <>
      <SeoHead
        title="关于我们-57漫画网-免费漫画阅读平台"
        description="57漫画网品牌介绍：专注下拉式免费阅读的漫画平台，海量韩漫无删减在线看，提供阅读元宇宙社交体验。联系我们：support@57mh.net，400-888-5757。"
        keywords="关于57漫画网,免费漫画平台,阅读元宇宙,联系57漫画网"
        canonicalPath="/about"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            <div className="mt-4 md:mt-5">
              <h1 className="font-display text-2xl font-bold text-foreground-900 md:text-3xl">关于57漫画网</h1>
              <p className="mt-2 max-w-2xl text-xs text-foreground-500 md:text-sm">
                免费 · 无删减 · 下拉式阅读，让每一部好漫画都被看见
              </p>
            </div>
          </div>
        </div>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14" aria-label="品牌介绍">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
            <div className="overflow-hidden rounded-2xl bg-background-200">
              <img
                src={aboutImage}
                alt="57漫画网编辑部工作场景"
                title="57漫画网品牌团队介绍"
                className="w-full object-cover object-top"
              />
            </div>
            <div>
              <span className="flex w-fit items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700">
                <i className="ri-quill-pen-fill h-4 w-4" aria-hidden="true"></i>
                品牌故事
              </span>
              <h2 className="mt-4 font-display text-xl font-bold leading-snug text-foreground-900 md:text-3xl">
                让每一次翻页，
                <br />
                都成为一场快乐冒险
              </h2>
              <div className="mt-4 space-y-3 text-xs leading-6 text-foreground-600 md:text-sm md:leading-7">
                <p>
                  57漫画网诞生于一群资深漫画编辑的执念：好的漫画，不应该被付费墙与删减拦在门外。我们以「下拉式免费阅读」为核心体验，整合韩漫、热血、玄幻、恋爱、悬疑等热门品类，为读者提供高清无删减的沉浸式追更体验。
                </p>
                <p>
                  除了高质量的阅读内容，我们还打造了「阅读元宇宙社交」——在虚拟漫画咖啡馆中，每位读者都能创建自己的化身，与同好围坐交流阅读心得、组队追更、参加每晚的主题分享会。我们相信，阅读不是孤单的旅程。
                </p>
                <p>
                  从今天起，《炸裂吧！巨棒》这样的热门作品与更多宝藏漫画，都在57漫画网等你免费开启。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-14" aria-label="平台数据">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <dl className="grid grid-cols-2 gap-4 lg:grid-cols-4 md:gap-6">
              {stats.map((s) => (
                <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 p-5 text-center md:p-6">
                  <dd className="font-display text-2xl font-bold text-primary-600 md:text-4xl">{s.value}</dd>
                  <dt className="mt-2 text-xs text-foreground-600 md:text-sm">{s.label}</dt>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14" aria-label="平台荣誉">
          <h2 className="mb-5 flex items-center gap-2.5 text-lg font-bold text-foreground-900 md:mb-7 md:text-xl">
            <span className="h-5 w-1.5 rounded-full bg-accent-500" aria-hidden="true"></span>
            平台声誉与荣誉
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
            {honors.map((honor) => (
              <article key={honor.year} className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 md:p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-award-fill h-5 w-5" aria-hidden="true"></i>
                </span>
                <p className="mt-4 text-xs font-bold text-primary-600 md:text-sm">{honor.year}</p>
                <h3 className="mt-1 text-sm font-bold text-foreground-900 md:text-base">{honor.title}</h3>
                <p className="mt-2 text-xs leading-5 text-foreground-500 md:text-sm">{honor.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-14" aria-label="使命愿景">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <h2 className="mb-5 flex items-center gap-2.5 text-lg font-bold text-foreground-900 md:mb-7 md:text-xl">
              <span className="h-5 w-1.5 rounded-full bg-primary-500" aria-hidden="true"></span>
              我们的使命
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
              {visions.map((v) => (
                <div key={v.title} className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-5 md:gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50">
                    <i className={`${v.icon} h-5 w-5`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-900 md:text-base">{v.title}</h3>
                    <p className="mt-1.5 text-xs leading-5 text-foreground-600 md:text-sm">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14" aria-label="联系我们">
          <h2 className="mb-5 flex items-center gap-2.5 text-lg font-bold text-foreground-900 md:mb-7 md:text-xl">
            <span className="h-5 w-1.5 rounded-full bg-primary-500" aria-hidden="true"></span>
            联系我们
          </h2>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
            <div className="flex flex-col gap-3">
              {contactItems.map((item) => (
                <div key={item.label} className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                    <i className={`${item.icon} h-5 w-5`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-0.5 text-sm font-bold text-foreground-900 md:text-base">{item.value}</p>
                  </div>
                </div>
              ))}
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <iframe
                  src="https://maps.google.com/maps?q=%E5%8C%97%E4%BA%AC%E5%B8%82%E6%9C%9D%E9%98%B3%E5%8C%BA%E5%BB%BA%E5%9B%BD%E8%B7%AF88%E5%8F%B7&t=&z=14&ie=UTF8&iwloc=&output=embed"
                  title="57漫画网所在位置地图"
                  className="h-64 w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h3 className="text-base font-bold text-foreground-900 md:text-lg">给我们留言</h3>
              <p className="mt-1.5 text-xs text-foreground-500 md:text-sm">
                合作洽谈、问题反馈或建议，都欢迎通过表单告诉我们，工作日 24 小时内回复。
              </p>
              <form
                data-readdy-form="57漫画网联系表单"
                onSubmit={handleSubmit}
                className="mt-5 flex flex-col gap-4"
                noValidate
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-1.5 block text-xs font-bold text-foreground-800 md:text-sm">
                      您的称呼 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      maxLength={30}
                      placeholder="怎么称呼您"
                      className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-xs placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-xs font-bold text-foreground-800 md:text-sm">
                      联系邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="name@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-xs placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="mb-1.5 block text-xs font-bold text-foreground-800 md:text-sm">
                    咨询类型
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                  >
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="内容建议">内容建议</option>
                    <option value="阅读反馈">阅读反馈</option>
                    <option value="商务广告">商务广告</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-1.5 block text-xs font-bold text-foreground-800 md:text-sm">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请简要描述您的需求或问题（500字以内）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-xs placeholder:text-foreground-400 focus:border-primary-400"
                  ></textarea>
                  <p className="mt-1 text-right text-[11px] text-foreground-400">500字以内</p>
                </div>
                <div className="hp-field" aria-hidden="true">
                  <label htmlFor="contact-website-alt">网站</label>
                  <input
                    id="contact-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === 'success' && (
                  <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm font-medium text-secondary-900" role="status">
                    <i className="ri-checkbox-circle-fill h-5 w-5" aria-hidden="true"></i>
                    提交成功！我们会尽快与您联系，感谢支持57漫画网。
                  </p>
                )}
                {status === 'error' && (
                  <p className="flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm font-medium text-accent-900" role="alert">
                    <i className="ri-error-warning-fill h-5 w-5" aria-hidden="true"></i>
                    {formError || '提交失败，请稍后重试'}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 md:text-base"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line h-5 w-5 animate-spin" aria-hidden="true"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill h-5 w-5" aria-hidden="true"></i>
                      提交留言
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}